import React, { useState, useMemo, useRef, useEffect } from 'react'
import sm from './SpecialModeView.module.scss'
import styles from './HangulPractice.module.scss'
import JamoBlock from './JamoBlock'
import { HANGUL, HANGUL_GROUPS } from '../data/drills/hangulLetters'
import { KOR_WORDS } from '../data/drills/korWords'
import { decomposeWord, soundOf, ROM_INITIAL, ROM_VOWEL, ROM_FINAL } from '../util/hangul'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import { celebrate, shake } from './juice'
import { cueCorrect, cueWrong, cueComplete } from './cues'
import useLetterStats, { troubleOf } from '../hooks/useLetterStats'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'

// Draw `n` items without replacement, each weighted by `w(item)`: the letters
// a learner keeps getting wrong come up more often, without ever being the
// only thing asked.
const weightedSample = (items, n, w) => {
  const pool = items.map(it => ({ it, w: Math.max(0.05, w(it)) }))
  const out = []
  while (out.length < n && pool.length) {
    const total = pool.reduce((a, p) => a + p.w, 0)
    let r = Math.random() * total
    let i = 0
    while (i < pool.length - 1 && (r -= pool[i].w) > 0) i++
    out.push(pool[i].it); pool.splice(i, 1)
  }
  return out
}

// Two practice modes for the Hangul alphabet board:
//   • HangulQuiz — "Test me": see a letter and pick its sound, or hear a sound
//     and pick the letter. Distractors come from the same group first (ㄱ ㅋ ㄲ),
//     which is where the real confusion is.
//   • HangulWordCloze — "In words": a real word with every letter's
//     romanization shown except one, highlighted; name that letter's sound.
//     Hearing the word is available as a hint, and a hint costs the streak.

const VOICE = voices.kor || voices.eng
const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]] } return b }
const ROUND = 12

// ── Test me ──────────────────────────────────────────────────────────────────
export function HangulQuiz({ voiceSpeed }) {
  const [groups, setGroups] = useState(['consonants', 'vowels'])
  const [dir, setDir] = useState('see') // see: letter → sound · hear: sound → letter
  const [focus, setFocus] = useState(true) // lean on the letters I get wrong
  const [round, setRound] = useState(null) // { qs, i, score, streak, answered, best }
  const cardRef = useRef(null)
  const { stats, record, weak } = useLetterStats('kor')
  const pool = useMemo(
    () => HANGUL_GROUPS.filter(([k]) => groups.includes(k)).flatMap(([k]) => HANGUL[k].map(([j, r, syl]) => ({ j, r, syl, group: k }))),
    [groups]
  )
  const toggleGroup = (k) => setGroups(g => (g.includes(k) ? (g.length > 1 ? g.filter(x => x !== k) : g) : [...g, k]))

  const start = () => {
    // Focus: a weak letter is drawn ~3× as often as a solid one; never only them.
    const picked = focus ? weightedSample(pool, Math.min(ROUND, pool.length), q => 1 + 3 * troubleOf(stats[q.j])) : shuffle(pool).slice(0, Math.min(ROUND, pool.length))
    const qs = picked.map(q => {
      const same = shuffle(pool.filter(x => x.group === q.group && x.j !== q.j))
      const others = shuffle(pool.filter(x => x.group !== q.group))
      const distract = same.slice(0, 3)
      while (distract.length < 3 && others.length) distract.push(others.shift())
      return { ...q, options: shuffle([q, ...distract]) }
    })
    setRound({ qs, i: 0, score: 0, streak: 0, best: 0, answered: null })
  }
  const q = round && round.qs[round.i]
  // In "hear" mode the question is the sound, so play it when it comes up.
  useEffect(() => {
    if (!q || dir !== 'hear') return
    const t = setTimeout(() => speak(q.syl, VOICE, voiceSpeed), 250)
    return () => clearTimeout(t)
  }, [q, dir, voiceSpeed])

  const pick = (opt) => {
    if (!q || round.answered) return
    const right = opt.j === q.j
    record(q.j, right) // the letter asked about; a wrong pick is that letter's miss
    speak(q.syl, VOICE, voiceSpeed)
    if (right) { cueCorrect(); const r = cardRef.current?.getBoundingClientRect(); if (r) celebrate(r.left + r.width / 2, r.top + 30, { count: 18, spread: 120 }) }
    else { cueWrong(); shake(cardRef.current) }
    setRound(s => {
      const streak = right ? s.streak + 1 : 0
      return { ...s, answered: opt.j, score: s.score + (right ? 1 : 0), streak, best: Math.max(s.best, streak) }
    })
    if (right) setTimeout(next, 900)
  }
  const next = () => setRound(s => {
    if (!s) return s
    if (s.i + 1 >= s.qs.length) cueComplete()
    return { ...s, i: s.i + 1, answered: null }
  })

  if (!round) {
    return (
      <div className={styles.wrap}>
        <div className={sm.field}>
          <span className={sm.fieldLabel}>Letters</span>
          <div className={sm.chipRow}>
            {HANGUL_GROUPS.map(([k, name]) => (
              <button key={k} className={`${sm.chip} ${groups.includes(k) ? sm.chipActive : ''}`} onClick={() => toggleGroup(k)}>{name}</button>
            ))}
          </div>
        </div>
        <div className={sm.field}>
          <span className={sm.fieldLabel}>Direction</span>
          <div className={sm.chipRow}>
            <button className={`${sm.chip} ${dir === 'see' ? sm.chipActive : ''}`} onClick={() => setDir('see')}>See the letter → pick the sound</button>
            <button className={`${sm.chip} ${dir === 'hear' ? sm.chipActive : ''}`} onClick={() => setDir('hear')}>Hear the sound → pick the letter</button>
          </div>
        </div>
        <div className={sm.field}>
          <span className={sm.fieldLabel}>Focus</span>
          <div className={sm.chipRow}>
            <button className={`${sm.chip} ${focus ? sm.chipActive : ''}`} onClick={() => setFocus(true)}>Lean on what I get wrong{weak.length ? ` (${weak.join(' ')})` : ''}</button>
            <button className={`${sm.chip} ${!focus ? sm.chipActive : ''}`} onClick={() => setFocus(false)}>Everything evenly</button>
          </div>
        </div>
        <button className={styles.primary} onClick={start}>Start · {Math.min(ROUND, pool.length)} questions</button>
      </div>
    )
  }

  if (round.i >= round.qs.length) {
    const pct = Math.round((round.score / round.qs.length) * 100)
    return (
      <div className={styles.wrap}>
        <div className={styles.big}>{pct}%</div>
        <p className={styles.muted}>{round.score} of {round.qs.length} right · best streak {round.best}</p>
        <div className={styles.row}>
          <button className={styles.primary} onClick={start}>Again</button>
          <button className={styles.ghost} onClick={() => setRound(null)}>Change letters</button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.meta}><span>{round.i + 1} / {round.qs.length}</span><span>streak {round.streak}</span></div>
      <div className={styles.qcard} ref={cardRef}>
        {dir === 'see'
          ? <><div className={styles.letter}>{q.j}</div><div className={styles.prompt}>What sound is this?</div></>
          : <><button className={styles.hearBig} onClick={() => speak(q.syl, VOICE, voiceSpeed)} title="Hear it again"><VolumeUpIcon /></button><div className={styles.prompt}>Which letter did you hear?</div></>}
        <div className={styles.options}>
          {q.options.map(opt => {
            const isRight = opt.j === q.j
            const cls = [styles.opt, round.answered && isRight ? styles.optRight : '', round.answered === opt.j && !isRight ? styles.optWrong : ''].join(' ')
            return (
              <button key={opt.j} className={cls} disabled={!!round.answered} onClick={() => pick(opt)}>
                {dir === 'see' ? opt.r : opt.j}
                {round.answered ? <span className={styles.optSub}>{dir === 'see' ? opt.j : opt.r} · {opt.syl}</span> : null}
              </button>
            )
          })}
        </div>
        {round.answered && round.answered !== q.j ? <button className={styles.primary} onClick={next}>Next →</button> : null}
      </div>
    </div>
  )
}

// ── In words ─────────────────────────────────────────────────────────────────
// Answer options by role: initials, vowels, and finals by sound class.
const uniq = (a) => Array.from(new Set(a))
const OPTIONS = { L: uniq(ROM_INITIAL), V: uniq(ROM_VOWEL), T: uniq(ROM_FINAL.filter(Boolean)) }
const label = (rom) => rom || 'silent'
const ROLE_NAME = { L: 'first consonant', V: 'vowel', T: 'final consonant' }

const buildWords = (lengths, count) =>
  shuffle(lengths.flatMap(n => (KOR_WORDS[n] || []).map(([k, e]) => ({ k, e })))).slice(0, count)

export function HangulWordCloze({ voiceSpeed }) {
  const [lengths, setLengths] = useState([1, 2, 3])
  const [round, setRound] = useState(null) // { items, i, score, streak, best }
  const [target, setTarget] = useState(null) // { si, pi, part, options }
  const [answered, setAnswered] = useState(null)
  const [hinted, setHinted] = useState(false)
  const cardRef = useRef(null)
  const { stats, record } = useLetterStats('kor')
  // Read through a ref: the stats steer which letter is hidden, but they must
  // not re-pick the target mid-word every time an answer is recorded.
  const statsRef = useRef(stats)
  statsRef.current = stats

  const item = round && round.items[round.i]
  const syls = useMemo(() => (item ? decomposeWord(item.k) : []), [item])

  // Pick which letter to hide, once per word — leaning on the letters this
  // learner gets wrong, when the word has one.
  useEffect(() => {
    if (!item) { setTarget(null); return }
    const spots = syls.flatMap((s, si) => s.parts.map((p, pi) => ({ si, pi, part: p })))
    const t = weightedSample(spots, 1, sp => 1 + 3 * troubleOf(statsRef.current[sp.part.jamo]))[0]
    const rom = t.part.rom
    const distract = shuffle(OPTIONS[t.part.role].filter(r => r !== rom)).slice(0, 3)
    setTarget({ ...t, options: shuffle([rom, ...distract]) })
    setAnswered(null); setHinted(false)
  }, [item, syls])

  const start = () => setRound({ items: buildWords(lengths, 10), i: 0, score: 0, streak: 0, best: 0 })
  const next = () => setRound(s => {
    if (!s) return s
    if (s.i + 1 >= s.items.length) cueComplete()
    return { ...s, i: s.i + 1 }
  })
  const hint = () => { setHinted(true); speak(item.k, VOICE, voiceSpeed) }
  const pick = (rom) => {
    if (!target || answered != null) return
    const right = rom === target.part.rom
    setAnswered(rom)
    record(target.part.jamo, right)
    speak(soundOf(target.part), VOICE, voiceSpeed)
    setTimeout(() => speak(item.k, VOICE, voiceSpeed), 700)
    if (right) { cueCorrect(); const r = cardRef.current?.getBoundingClientRect(); if (r) celebrate(r.left + r.width / 2, r.top + 30, { count: 18, spread: 120 }) }
    else { cueWrong(); shake(cardRef.current) }
    setRound(s => {
      const streak = right && !hinted ? s.streak + 1 : 0
      return { ...s, score: s.score + (right ? 1 : 0), streak, best: Math.max(s.best, streak) }
    })
  }

  if (!round) {
    return (
      <div className={styles.wrap}>
        <div className={sm.field}>
          <span className={sm.fieldLabel}>Syllables per word</span>
          <div className={sm.chipRow}>
            {[1, 2, 3].map(n => (
              <button key={n} className={`${sm.chip} ${lengths.includes(n) ? sm.chipActive : ''}`}
                onClick={() => setLengths(l => (l.includes(n) ? (l.length > 1 ? l.filter(x => x !== n) : l) : [...l, n].sort()))}>{n}</button>
            ))}
          </div>
        </div>
        <p className={styles.muted}>A real word, every letter's sound shown except one. Read the word and name the highlighted letter's sound. Hearing the word is a hint — it costs the streak, not the point.</p>
        <button className={styles.primary} onClick={start}>Start · 10 words</button>
      </div>
    )
  }
  if (round.i >= round.items.length) {
    const pct = Math.round((round.score / round.items.length) * 100)
    return (
      <div className={styles.wrap}>
        <div className={styles.big}>{pct}%</div>
        <p className={styles.muted}>{round.score} of {round.items.length} right · best streak {round.best}</p>
        <div className={styles.row}>
          <button className={styles.primary} onClick={start}>Again</button>
          <button className={styles.ghost} onClick={() => setRound(null)}>Change words</button>
        </div>
      </div>
    )
  }
  if (!target) return null

  const isTarget = (si, pi) => target.si === si && target.pi === pi
  const targetState = answered == null ? 'lit' : answered === target.part.rom ? 'hit' : 'miss'
  return (
    <div className={styles.wrap}>
      <div className={styles.meta}><span>{round.i + 1} / {round.items.length}</span><span>streak {round.streak}</span></div>
      <div className={styles.qcard} ref={cardRef}>
        <div className={styles.blocks}>
          {syls.map((syl, si) => (
            <JamoBlock key={si} syl={syl} size={syls.length > 2 ? 96 : 116}
              states={Object.fromEntries(syl.parts.map((p, pi) => [p.role, isTarget(si, pi) ? targetState : 'idle']))} />
          ))}
        </div>
        <div className={styles.strip}>
          {syls.map((syl, si) => (
            <span key={si} className={styles.stripSyl}>
              {syl.parts.map((p, pi) => (
                <span key={pi} className={`${styles.stripPart} ${isTarget(si, pi) ? styles['sp_' + targetState] : ''}`}>
                  <span className={styles.stripJamo}>{p.jamo}</span>
                  <span className={styles.stripRom}>{isTarget(si, pi) && answered == null ? '?' : label(p.rom)}</span>
                </span>
              ))}
            </span>
          ))}
        </div>
        <div className={styles.prompt}>What sound is the highlighted {ROLE_NAME[target.part.role]}?</div>
        <div className={styles.options}>
          {target.options.map(rom => {
            const isRight = rom === target.part.rom
            const cls = [styles.opt, answered != null && isRight ? styles.optRight : '', answered === rom && !isRight ? styles.optWrong : ''].join(' ')
            return <button key={rom || '_silent'} className={cls} disabled={answered != null} onClick={() => pick(rom)}>{label(rom)}</button>
          })}
        </div>
        <div className={styles.row}>
          {answered == null
            ? <button className={`${styles.ghost} ${hinted ? styles.ghostOn : ''}`} onClick={hint}><VolumeUpIcon fontSize="small" /> hint: hear the word</button>
            : <>
                <span className={styles.reveal}>{item.k}{item.e ? ` · ${item.e}` : ''}</span>
                <button className={styles.ghost} onClick={() => speak(item.k, VOICE, voiceSpeed)}><VolumeUpIcon fontSize="small" /> again</button>
                <button className={styles.primary} onClick={next}>Next →</button>
              </>}
        </div>
      </div>
    </div>
  )
}
